import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'redux/store';
import styled from '@emotion/styled';
import { Box, Divider, Grid, Stack, Typography } from '@mui/material';

import DetailItem from 'components/Staff/ItemDetail';
import Button from 'components/Common/Button';
import TextInput from 'components/Common/TextInput';
import { dateFormat } from 'utils/helpers';
import MuiLoader from 'components/Common/Loader';
import { fetchParishMemberDetails, updateParishionerDetails } from 'redux/slices/community/parishMemberDetailSlice';
import { selectHasPortalStaffAccess, selectSelectedParishId } from 'redux/selectors/parishContext';
import { parishionerIdClearValue, type Member } from 'types/members';

const InnerContainer = styled('div')({
    backgroundColor: 'white',
    paddingTop: '4vh',
    paddingBottom: 16,
    marginTop: 12,
});

const PARISHIONER_ID_MAX_LENGTH = 100;

function ParishonerUserDetailContainer() {
    const dispatch = useDispatch<AppDispatch>();
    const { uuid } = useParams();
    const [saving, setSaving] = useState(false);
    const { details, loading: parishionerDetailLoading } = useSelector((state: RootState) => state.community.parishionerDetails);
    const canEdit = useSelector(selectHasPortalStaffAccess);
    const parishId = useSelector(selectSelectedParishId);
    const member = (details || {}) as Partial<Member>;
    const { name = '', email = '', username = '', joinedAt = '', status = '' } = member;
    const savedParishionerId = member.parishionerId || '';
    const [parishionerId, setParishionerId] = useState(savedParishionerId);

    useEffect(() => {
        if (uuid && parishId) {
            dispatch(fetchParishMemberDetails({ uuid, parishId }));
        }
    }, [uuid, parishId, dispatch]);

    // Loaded or saved member → reset the field to what the server now holds.
    useEffect(() => {
        setParishionerId(savedParishionerId);
    }, [savedParishionerId]);

    const trimmed = parishionerId.trim();
    // Save compares the trimmed value (surrounding spaces are never stored); Discard shows for ANY
    // edit, including one that only adds spaces, so the field can always be reset.
    const isDirty = trimmed !== savedParishionerId;
    const isEdited = parishionerId !== savedParishionerId;
    const error =
        trimmed.length > PARISHIONER_ID_MAX_LENGTH ? `Parishioner ID must be at most ${PARISHIONER_ID_MAX_LENGTH} characters.` : '';
    const canSave = canEdit && isDirty && !error && !saving;

    const handleSave = async () => {
        if (!uuid || !canSave) return;
        setSaving(true);
        // Re-sends the existing custom keys alongside parishionerId: harmless now that the backend merges
        // `custom` key-by-key, and it keeps other custom keys safe against a backend without that merge.
        const existingCustom =
            member.additionalInfo?.custom && typeof member.additionalInfo.custom === 'object' ? member.additionalInfo.custom : {};
        // Success → the slice replaces `details` with the PATCH response, which resets the field.
        // Failure → the middleware toasts the API message and the typed value stays put.
        await dispatch(
            updateParishionerDetails({
                userId: uuid,
                payload: { custom: { ...existingCustom, parishionerId: trimmed || parishionerIdClearValue(member.additionalInfo) } },
            })
        );
        setSaving(false);
    };

    return (
        <InnerContainer>
            <MuiLoader loading={parishionerDetailLoading || (!!uuid && !parishId)} absolute />
            <Box mx={{ xs: 2, md: 8 }} style={{ marginTop: 16 }}>
                {/* The SAVED id (not the value being typed below), so it only changes after a successful Save. */}
                <Typography sx={{ fontSize: 16, fontWeight: 400, my: 1, pl: '6px' }} data-testid="parishioner-id-heading">
                    Parishioner id: {savedParishionerId || '—'}
                </Typography>
                <Divider sx={{ backgroundColor: '#6B7280' }} />
                <Grid sx={{ my: 3 }} container spacing={2}>
                    <Grid item xs={12}>
                        <DetailItem label="Name" value={name} />
                        <DetailItem label="Email" value={email || username} />
                        <DetailItem label="Status" value={status || 'Member'} />
                        <DetailItem label="Joined" value={joinedAt ? dateFormat(joinedAt) : ''} />
                    </Grid>
                </Grid>

                {canEdit && (
                    <Stack gap={3} data-testid="parishioner-form">
                        <TextInput
                            icon={null}
                            width="100%"
                            label="Parishioner ID #"
                            placeholder="e.g. SB-10482"
                            value={parishionerId}
                            onChange={(e) => setParishionerId(e.target.value)}
                            error={!!error}
                            // Only the validation message — no standing hint under the field.
                            helperText={error || undefined}
                            disabled={saving}
                        />
                        <Stack direction="row" justifyContent="center" gap={2} mt={2}>
                            {isEdited && (
                                <Button
                                    variant="outlined"
                                    backgroundColor="white"
                                    onClick={() => setParishionerId(savedParishionerId)}
                                    disabled={saving}
                                    sx={{ width: { xs: '50%', sm: '25%' } }}
                                    title="Discard"
                                />
                            )}
                            <Button
                                onClick={handleSave}
                                disabled={!canSave}
                                loading={saving}
                                sx={{ width: { xs: '100%', sm: '40%' } }}
                                title="Save"
                            />
                        </Stack>
                    </Stack>
                )}
            </Box>
        </InnerContainer>
    );
}

export default ParishonerUserDetailContainer;
